import React, { useState } from 'react';
import { Button, message } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import RichTextEditor from '../../../components/RichTextEditor';
import type { RichTextEditorProps } from '../../../components/RichTextEditor';
import type { ProgramReview } from '../../../interfaces/CamProgramming.interface';
import { useUpdateProgramReviewMutation } from '../../../redux/api/manufacturing';
import { hasRichTextContent } from '../fields';

interface EmbedReviewFeedbackProps {
	review: ProgramReview;
	programId: string;
	/** Image handlers from `useProgramImages`, shared with the issue editor. */
	images: Pick<RichTextEditorProps, 'onUploadImage' | 'onImportImageUrl'>;
}

/**
 * The review's own feedback, kept within reach at the foot of the panel.
 *
 * A review carries two different things: issues, logged one after another as
 * they are found, and a single piece of feedback summing the program up. In a
 * window as short as the Fusion palette only one of them can have the room, so
 * issues keep it and this collapses to a single line until it is wanted.
 */
const EmbedReviewFeedback: React.FC<EmbedReviewFeedbackProps> = ({
	review,
	programId,
	images,
}) => {
	const [open, setOpen] = useState(false);
	// Null until the editor has been opened and handed the stored feedback.
	// Treating an unopened draft as empty would read as "the feedback was just
	// cleared" and offer a Save that wipes it.
	const [draft, setDraft] = useState<string | null>(null);
	// The editor reads `content` once, as a starting value, so remounting it is
	// what loads the stored feedback in.
	const [editorKey, setEditorKey] = useState(0);

	const [updateReview, { isLoading: isSaving }] =
		useUpdateProgramReviewMutation();

	const saved = review.feedback ?? '';
	// An emptied editor still reports an empty paragraph, which would otherwise
	// read as a change against feedback that was never written.
	const visible = (html: string) => (hasRichTextContent(html) ? html : '');
	const isDirty = draft !== null && visible(draft) !== visible(saved);

	const toggle = () => {
		if (!open) {
			setDraft(saved);
			setEditorKey((key) => key + 1);
		}
		setOpen((wasOpen) => !wasOpen);
	};

	const handleSave = async () => {
		try {
			await updateReview({
				id: review.id,
				programId,
				values: { feedback: visible(draft ?? saved) },
			}).unwrap();
			message.success('Feedback saved');
		} catch {
			message.error('Could not save feedback');
		}
	};

	return (
		<section className="embed-feedback">
			<div className="embed-feedback__bar">
				<button
					type="button"
					className="embed-feedback__toggle"
					onClick={toggle}
					aria-expanded={open}
				>
					{open ? <DownOutlined /> : <RightOutlined />}
					<span>Review Feedback</span>
					{!open && hasRichTextContent(saved) ? (
						<span className="embed-feedback__marker">written</span>
					) : null}
				</button>
				{/* Stays put while collapsed if there is something unsaved, so
				    folding the panel away cannot quietly drop it. */}
				{open || isDirty ? (
					<Button
						type="primary"
						disabled={!isDirty}
						loading={isSaving}
						onClick={handleSave}
					>
						Save
					</Button>
				) : null}
			</div>

			{open ? (
				<div className="embed-feedback__body">
					<RichTextEditor
						key={editorKey}
						content={draft ?? saved}
						onChange={setDraft}
						placeholder="Overall notes on this program…"
						minHeight={96}
						{...images}
					/>
				</div>
			) : null}
		</section>
	);
};

export default EmbedReviewFeedback;
